import { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthProvider";
import { completeProfile } from "../../../services/auth/utils";
import type { TVerifyOtpRequest } from "../../../services/auth/types";
import { sendUtm } from "../../../services/utm";
import { getWebinar } from "../../../services/webinar";
import { getUtmData } from "../../../utils";
import type { ProfileForm } from "../schema/auth.scehma";
import { MobileStep, OtpStep, ProfileStep } from "../steps";

export type LoginStep = 1 | 2 | 3;

export const LoginCard = () => {
  const [step, setStep] = useState<LoginStep>(1);
  const [phone, setPhone] = useState("");
  const [verifyData, setVerifyData] = useState<
    TVerifyOtpRequest["response"] | null
  >(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  const { mutate: submitProfile, isPending: isProfilePending } = useMutation({
    mutationFn: async (data: ProfileForm) => {
      if (!verifyData) {
        throw new Error("اطلاعات ورود پیدا نشد");
      }

      return completeProfile({
        fullName: data.fullName,
        style: data.voiceStyle,
        token: verifyData.token,
      });
    },

    onSuccess: async (profileResponse) => {
      const { token, tokenExpires } = verifyData ?? {};

      if (!token || !tokenExpires) {
        toast.error("اطلاعات ورود پیدا نشد");
        return;
      }

      /*
       * ----------------------------------------
       * 1. Login
       * ----------------------------------------
       */
      login(profileResponse.user, token, tokenExpires);

      toast.success("ورود با موفقیت انجام شد 🎉");

      /*
       * ----------------------------------------
       * 2. Send UTM - Only once
       * ----------------------------------------
       */
      const hasSentUtm = localStorage.getItem("utm_sent");

      if (!hasSentUtm) {
        const utm = getUtmData();

        sendUserUtm(utm, {
          onSuccess: () => {
            localStorage.setItem("utm_sent", "true");
          },
        });
      }

      /*
       * ----------------------------------------
       * 3. Register in Webinar
       * ----------------------------------------
       */
      getWebinarEnroll(
        {
          webinarId: 6,
        },
        {
          onSuccess: () => {
            /*
             * ----------------------------------------
             * 4. Navigate (API returns the mini-course path)
             * ----------------------------------------
             */
            navigate(profileResponse.redirectTo);
          },

          onError: () => {
            toast.error("کاربر در وبینار ثبت نشد");
          },
        },
      );
    },

    onError: () => {
      toast.error("ثبت اطلاعات کاربر با خطا مواجه شد");
    },
  });

  /*
   * ----------------------------------------
   * UTM Mutation
   * ----------------------------------------
   */
  const { mutate: sendUserUtm } = useMutation({
    mutationFn: sendUtm,
  });

  /*
   * ----------------------------------------
   * Webinar Mutation
   * ----------------------------------------
   */
  const { mutate: getWebinarEnroll } = useMutation({
    mutationFn: getWebinar,
  });

  /*
   * ----------------------------------------
   * Profile Submit
   * ----------------------------------------
   */
  const handleProfileSuccess = (data: ProfileForm) => {
    submitProfile(data);
  };

  return (
    <div className="relative mb-10 w-92.25 h-118.25 md:w-217.5 md:h-162 overflow-hidden rounded-[31px] flex flex-col items-center justify-center shadow-[0_-63px_165.1px_-77px_rgba(84,198,249,0.37)]">
      {/* Image */}
      <img
        src="/images/login-card-bg.jpeg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gray-300/40" />
      <div className="relative z-10 w-86.25 h-104.5">
        <div className="flex flex-col md:items-center justify-end md:justify-between h-full w-full">
          <img
            src="/images/vac-logo.png"
            alt="VAC"
            className="h-22.25 w-47.25 hidden md:block"
          />

          <div className="flex flex-col justify-center gap-4.75 md:gap-3.5">
            <h1 className="md:block hidden text-center text-heading-3 text-white font-kalameh">
              ورود به مینی دوره صداسازی
            </h1>

            <h1 className="md:hidden block text-heading-3 text-white font-kalameh">
              <span className="block md:inline">ورود به</span>{" "}
              <span className="block md:inline">مینی دوره صداسازی</span>
            </h1>

            <p className="md:text-center text-body-2 font-kalameh text-white">
              {step === 1 && "برای شروع شماره همراه خود را وارد کن"}
              {step === 2 && "کد تأیید ارسال شده را وارد کن"}
              {step === 3 && "لطفا اطلاعات زیر رو تکمیل کن"}
            </p>
          </div>

          <div className="mb-3.5 mt-8 w-full">
            {step === 1 && (
              <MobileStep
                onSuccess={(value) => {
                  setPhone(value);
                  setStep(2);
                }}
              />
            )}

            {step === 2 && (
              <OtpStep
                phone={phone}
                onSuccess={(data) => {
                  setVerifyData(data);
                  setStep(3);
                }}
              />
            )}

            {step === 3 && (
              <ProfileStep
                onSuccess={handleProfileSuccess}
                isPending={isProfilePending}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
